import {
  VpButton,
  VpDivider,
  VpIcon,
  VpRadioGroup,
  VpRadioGroupItem,
} from '@vtmn-play/react';
import clsx from 'clsx';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';

export enum StatusFilter {
  CREATED = 'Created',
  PROPOSAL_VALIDATED = 'Proposal Validated',
  VALIDATED = 'Validated',
  CANCELLED = 'Cancelled',
}

export type StatusState = {
  status: string;
  disabled: boolean;
};

export type SelectStatusInputProps = {
  selectedStatus: string;
  setSelectedStatus: (status: string) => void;
  availableStatus: StatusState[];
  label?: string;
};

export const SelectStatusInput = ({
  selectedStatus,
  setSelectedStatus,
  availableStatus,
  label,
}: SelectStatusInputProps) => {
  const { t } = useTranslation();
  const [deployed, setDeployed] = useState(false);
  const [tempSelectedStatus, setTempSelectedStatus] = useState('');

  const selectContainerRef: React.RefObject<HTMLInputElement | null> =
    useRef(null);
  const radioGroupRef = useRef(null);

  useEffect(() => {
    document.addEventListener('click', () => {
      if (document.activeElement !== selectContainerRef.current) {
        setDeployed(false);
      }
    });
  }, []);

  const toggleDeploy = () => {
    if (selectContainerRef.current === document.activeElement) {
      setDeployed(!deployed);
    }
  };

  const validateStatus = () => {
    setSelectedStatus(tempSelectedStatus);
    setDeployed(false);
  };

  const resetStatus = () => {
    setSelectedStatus('');
    setTempSelectedStatus('');
    setDeployed(false);
  };

  const defaultRadioValue =
    !tempSelectedStatus && !!selectedStatus
      ? selectedStatus
      : tempSelectedStatus;

  return (
    <div>
      {label && (
        <div className={styles['spo-status-select-label']}>{label}</div>
      )}
      <div
        className={styles['spo-status-dropdown']}
        ref={selectContainerRef}
        data-testid="status-dropdown"
        tabIndex={0}
        role="listbox"
        onClick={toggleDeploy}
        onKeyDown={toggleDeploy}
        aria-label="status dropdown"
      >
        <div
          className={clsx(styles['spo-dropdown-input'], {
            [styles['spo-dropdown-input-active']]: deployed,
          })}
        >
          {selectedStatus ? (
            <span data-testid={`mass-edit-inputs_status-${selectedStatus}`}>
              {StatusFilter[selectedStatus as keyof typeof StatusFilter]}
            </span>
          ) : (
            <span className={styles['spo-dropdown-input-placeholder']}>
              {t('mass-edit-inputs.status_placeholder')}
            </span>
          )}
          <VpIcon
            name={deployed ? 'chevron-up' : 'chevron-down'}
            size={16}
            data-testid="dropdown-status-chevron"
          />
        </div>
        {deployed && (
          <div
            className={styles['spo-dropdown-list']}
            role="list"
            data-testid="dropdown-status-list"
          >
            <VpRadioGroup
              name="contact"
              ref={radioGroupRef}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                setTempSelectedStatus(event.target.value)
              }
              onClick={(event: Event) => event.stopPropagation()}
              value={defaultRadioValue}
            >
              {availableStatus.map((availableStatus) => (
                <VpRadioGroupItem
                  id={`status-radio-${availableStatus.status}`}
                  value={availableStatus.status}
                  data-testid={`${availableStatus.status}-radio-item`}
                  key={availableStatus.status}
                  disabled={availableStatus.disabled}
                  onClick={(event: Event) => event.stopPropagation()}
                >
                  {
                    StatusFilter[
                      availableStatus.status as keyof typeof StatusFilter
                    ]
                  }
                </VpRadioGroupItem>
              ))}
            </VpRadioGroup>
            <VpDivider className="spo-select-status-input" />
            <div className={styles['spo-dropdown-buttons']}>
              <VpButton
                data-testid="status-select_cancel-button"
                shape="squared"
                variant="tertiary"
                size="small"
                onClick={resetStatus}
              >
                {t('cancel')}
              </VpButton>
              <VpButton
                data-testid="status-select_save-button"
                shape="squared"
                size="small"
                onClick={validateStatus}
              >
                {t('save')}
              </VpButton>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
